import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { remapEchartsColors, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { ScatterChart } from 'echarts/charts';
import { GridComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { HOVER_VALUE_CHART_COLOR, type BinSelection, type HistogramOverlay } from '../measures/measures-highlight.util';
import { buildOverlaySeries, fmt } from '../chart-shared';
import { DEFAULT_DOT_PLOT_DISPLAY, type DotPlotDisplayConfig, type DotPlotParams } from './dot-plot-chart.model';

echarts.use([ScatterChart, SVGRenderer, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent]);

/**
 * Approximate usable grid height in pixels (container minus grid padding).
 * Used to compute yMax so that 1 y-unit ≈ dotSize pixels, making dots
 * appear tightly stacked from the baseline.
 */
const GRID_PX_ESTIMATE = 130;

@Component({
  selector: 'dojo-dot-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './dot-plot-chart.component.html',
  styleUrl: './dot-plot-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full h-full' },
})
export class DotPlotChartComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;

  readonly params = input.required<DotPlotParams>();
  readonly display = input<DotPlotDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });
  readonly selectedBin = input<BinSelection | null>(null);
  readonly hoveredValue = input<number | null>(null);

  readonly binSelect = output<BinSelection>();
  readonly initOpts = { renderer: 'svg' } as const;

  private readonly stacking = computed(() => {
    const data = this.params().data;
    if (data.length === 0) {
      return null;
    }
    const step = computeStep(data);
    return { step, stacked: stackDots(data, step) };
  });

  readonly chartOptions = computed<EChartsOption>(() => {
    const display = { ...DEFAULT_DOT_PLOT_DISPLAY, ...this.display() };
    const overlay = this.overlay();
    const selectedBin = this.selectedBin();
    const hoveredValue = this.hoveredValue();

    const result = this.stacking();
    if (!result) {
      return buildEmptyChart();
    }

    const { step, stacked } = result;

    // Dynamic dot size: shrink when many unique values to avoid overlap
    let dotSize = display.dotSize ?? 10;
    if (stacked.uniqueCount > 40) {
      dotSize = Math.min(dotSize, 5);
    } else if (stacked.uniqueCount > 25) {
      dotSize = Math.min(dotSize, 6);
    } else if (stacked.uniqueCount > 15) {
      dotSize = Math.min(dotSize, 8);
    }

    // yMax is set so that 1 y-unit ≈ dotSize pixels.
    // This keeps dots visually packed tight from the baseline.
    const tightMax = Math.ceil(GRID_PX_ESTIMATE / dotSize);
    const yMax = Math.max(stacked.maxStack + 1, tightMax);

    const bold = (v: string | number) => `<span style="font-weight:600;color:#111827">${v}</span>`;

    const halfStep = step / 2;
    const scatterData = stacked.points.map((p) => {
      const isSelected = selectedBin && p.value[0] >= selectedBin.min && p.value[0] <= selectedBin.max;
      const isHovered =
        hoveredValue !== null && p.value[0] >= hoveredValue - halfStep && p.value[0] <= hoveredValue + halfStep;
      return {
        ...p,
        itemStyle: isSelected ? { color: '#fbbf24' } : isHovered ? { color: HOVER_VALUE_CHART_COLOR } : undefined,
      };
    });

    const series: NonNullable<EChartsOption['series']> = [
      {
        type: 'scatter',
        name: 'Dot Plot',
        data: scatterData,
        symbolSize: dotSize,
        itemStyle: {
          color: display.dotColor ?? '#6366f1',
        },
      },
      buildOverlaySeries(overlay),
    ];

    return {
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (params: unknown) => {
          const p = params as { seriesName?: string; data?: DotPlotPoint };
          if (p.seriesName !== 'Dot Plot' || !p.data) {
            return '';
          }
          const val = p.data.value[0];
          const freq = p.data.frequency;
          return [
            `<div style="margin-bottom:4px;font-weight:600;color:#6366f1">${fmt(val)}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Frequency</span>${bold(
              freq,
            )}</div>`,
          ].join('');
        },
      },
      grid: {
        top: 40,
        bottom: 25,
        left: 20,
        right: 20,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Value',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { show: false },
        axisLabel: { fontSize: 12, color: '#6b7280' },
        min: stacked.xMin - step,
        max: stacked.xMax + step,
      },
      yAxis: {
        type: 'value',
        show: false,
        min: 0,
        max: yMax,
      },
      series,
    };
  });

  readonly adaptedOptions = computed(() => remapEchartsColors(this.chartOptions(), this.document, this.isDark()));

  onChartClick(event: { seriesType?: string; seriesName?: string; data?: unknown }): void {
    const result = this.stacking();
    if (!result) {
      return;
    }
    if (event.seriesType !== 'scatter' || event.seriesName !== 'Dot Plot') {
      return;
    }

    const point = event.data as DotPlotPoint | undefined;
    if (!point?.value) {
      return;
    }

    const rounded = point.value[0];
    const halfStep = result.step / 2;
    this.binSelect.emit({
      dataIndex: Math.round(rounded * 10000),
      min: rounded - halfStep,
      max: rounded + halfStep,
    });
  }
}

// ---------------------------------------------------------------------------
// Stacking logic
// ---------------------------------------------------------------------------

interface DotPlotPoint {
  readonly value: [number, number];
  readonly frequency: number;
}

interface StackedResult {
  readonly points: DotPlotPoint[];
  readonly xMin: number;
  readonly xMax: number;
  readonly maxStack: number;
  readonly uniqueCount: number;
}

/**
 * Compute a rounding step for grouping values.
 * - If all values are integers, step = 1.
 * - Otherwise, use the smallest non-zero difference between sorted
 *   unique values, rounded to a clean number.
 */
function computeStep(data: number[]): number {
  const allIntegers = data.every((v) => Number.isInteger(v));
  if (allIntegers) {
    return 1;
  }

  const unique = [...new Set(data)].sort((a, b) => a - b);
  if (unique.length < 2) {
    return 1;
  }

  let minDiff = Infinity;
  for (let i = 1; i < unique.length; i++) {
    const diff = unique[i] - unique[i - 1];
    if (diff > 0 && diff < minDiff) {
      minDiff = diff;
    }
  }

  if (minDiff === Infinity || minDiff === 0) {
    return 1;
  }

  const magnitude = Math.pow(10, Math.floor(Math.log10(minDiff)));
  const normalized = minDiff / magnitude;

  if (normalized <= 1) {
    return magnitude;
  }
  if (normalized <= 2) {
    return 2 * magnitude;
  }
  if (normalized <= 5) {
    return 5 * magnitude;
  }
  return 10 * magnitude;
}

function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function stackDots(data: number[], step: number): StackedResult {
  // First pass: compute total frequency per rounded value
  const frequencyMap = new Map<number, number>();
  for (const value of data) {
    const rounded = roundToStep(value, step);
    frequencyMap.set(rounded, (frequencyMap.get(rounded) ?? 0) + 1);
  }

  // Second pass: assign stack positions and attach frequency
  const currentStack = new Map<number, number>();
  const points: DotPlotPoint[] = [];
  let xMin = Infinity;
  let xMax = -Infinity;
  let maxStack = 0;

  for (const value of data) {
    const rounded = roundToStep(value, step);
    const stackPos = (currentStack.get(rounded) ?? 0) + 1;
    currentStack.set(rounded, stackPos);

    points.push({
      value: [rounded, stackPos],
      frequency: frequencyMap.get(rounded)!,
    });

    if (rounded < xMin) {
      xMin = rounded;
    }
    if (rounded > xMax) {
      xMax = rounded;
    }
    if (stackPos > maxStack) {
      maxStack = stackPos;
    }
  }

  return { points, xMin, xMax, maxStack, uniqueCount: frequencyMap.size };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildEmptyChart(): EChartsOption {
  return {
    grid: { top: 40, bottom: 25, left: 20, right: 20, containLabel: true },
    xAxis: {
      type: 'value',
      name: 'Value',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { show: false },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    yAxis: { type: 'value', show: false },
    series: [],
  };
}
